import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '@/lib/axios';

export interface ChatbotMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const chatbotHistoryKey = (charId: number) => ['chatbot', 'history', charId] as const;

async function fetchChatHistory(): Promise<ChatbotMessage[]> {
  const { data } = await api.get<{ status: string; data: { history: ChatbotMessage[] } }>(
    '/api/v1/chatbot/history',
  );
  return data.data?.history ?? [];
}

async function postChatMessage(message: string): Promise<{
  response: string;
  history: ChatbotMessage[];
}> {
  try {
    const { data } = await api.post<{
      status: string;
      data?: { response: string; history: ChatbotMessage[] };
      message?: string;
    }>('/api/v1/chatbot/message', { message });

    if (data.status !== 'success' || !data.data?.response) {
      throw new Error(data.message ?? 'Chat request failed');
    }

    return data.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const msg = (err.response?.data as { message?: string } | undefined)?.message;
      if (msg) throw new Error(msg);
    }
    throw err;
  }
}

export function useChatbotHistory(charId: number) {
  return useQuery({
    queryKey: chatbotHistoryKey(charId),
    queryFn: fetchChatHistory,
    staleTime: 0,
  });
}

export function useChatbotMutations(charId: number) {
  const queryClient = useQueryClient();

  const send = useMutation({
    mutationFn: postChatMessage,
    onSuccess: (result) => {
      queryClient.setQueryData(chatbotHistoryKey(charId), result.history);
    },
  });

  const clear = useMutation({
    mutationFn: async () => {
      await api.delete('/api/v1/chatbot/history');
    },
    onSuccess: () => {
      queryClient.setQueryData(chatbotHistoryKey(charId), []);
    },
  });

  return { send, clear };
}

export function prefetchChatbotHistory(queryClient: QueryClient, charId: number): void {
  void queryClient.prefetchQuery({
    queryKey: chatbotHistoryKey(charId),
    queryFn: fetchChatHistory,
    staleTime: 30_000,
  });
}
